import type {AlertaPersonalizada, AlertasDeSeguimiento} from '@cnc/contracts';
import {useState, type ReactNode} from 'react';
import {Link} from 'react-router';
import {useAlertasPropias, useEditarAlerta, useEliminarAlerta} from '../api/alertas';
import {ErrorDeApi} from '../api/cliente';
import {tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {FormularioDeAlerta} from './formulario-de-alerta';
import {usePermisos} from './sesion-contexto';

const VISIBLES = 5;

type Tono = 'vencida' | 'hoy' | 'proxima' | 'neutra';
const CLASE_DE_TONO: Record<Tono, string> = {
  vencida: 'border-alerta/40 bg-red-50',
  hoy: 'border-dorado/50 bg-dorado-claro',
  proxima: 'border-verde/30 bg-verde-claro/60',
  neutra: 'border-linea bg-papel',
};

// Las tres tarjetas de alertas: arribos, puerto (calculadas con la fecha de
// hoy) y las propias, que se crean, resuelven y borran aqui.
export function AlertasSeguimiento({
  alertas,
  cargando,
}: {
  alertas: AlertasDeSeguimiento | undefined;
  cargando: boolean;
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-apagado">{tr('Alertas', 'Alerts')}</h2>
        {alertas && (
          <span className="text-xs text-apagado">
            {tr('Calculadas con la fecha de hoy,', "Calculated with today's date,")} {fecha(alertas.hoy)}
          </span>
        )}
      </div>
      <div className={`mt-2 grid gap-4 lg:grid-cols-3 ${cargando ? 'opacity-60' : ''}`}>
        <Tarjeta
          titulo={tr('Arribos', 'Arrivals')}
          subtitulo={tr('Próximos 5 días y vencidos', 'Next 5 days and overdue')}
          cantidad={alertas?.arribos.length ?? 0}
          vacio={tr('Sin arribos para alertar.', 'No arrivals to flag.')}
          filas={(alertas?.arribos ?? []).map(a => ({
            clave: `${a.codigo}-${a.fecha}`,
            tono: a.tono,
            principal: (
              <>
                <Enlace codigo={a.codigo} /> · {a.exportador ?? tr('Terceros', 'Third party')}
              </>
            ),
            detalle: `${fecha(a.fecha)} · ${
              a.dias < 0
                ? tr(`${numero(-a.dias)} días vencida`, `${numero(-a.dias)} days overdue`)
                : a.dias === 0
                  ? tr('Arriba hoy', 'Arrives today')
                  : tr(`faltan ${numero(a.dias)} días`, `${numero(a.dias)} days to go`)
            } · ${a.estadoEtiqueta}`,
          }))}
        />
        <Tarjeta
          titulo={tr('Puerto', 'Port')}
          subtitulo={tr('Estadía y cobros', 'Stay and charges')}
          cantidad={alertas?.puerto.length ?? 0}
          vacio={tr('Sin exportaciones en puerto.', 'No exports in port.')}
          filas={(alertas?.puerto ?? []).map(p => ({
            clave: p.codigo,
            tono: p.diasConCobro > 0 ? 'vencida' : 'proxima',
            principal: (
              <>
                <Enlace codigo={p.codigo} /> ·{' '}
                {tr(`${numero(p.diasEnPuerto)} días en puerto`, `${numero(p.diasEnPuerto)} days in port`)}
              </>
            ),
            detalle: tr(
              `Llegó el ${fecha(p.llegada)} · ${numero(p.diasLibres)} libres · ${
                p.diasConCobro > 0
                  ? `${numero(p.diasConCobro)} sujetos a cobro`
                  : `quedan ${numero(p.diasLibresRestantes)}`
              }`,
              `Arrived on ${fecha(p.llegada)} · ${numero(p.diasLibres)} free · ${
                p.diasConCobro > 0
                  ? `${numero(p.diasConCobro)} subject to charges`
                  : `${numero(p.diasLibresRestantes)} left`
              }`,
            ),
          }))}
        />
        <AlertasPropias alertas={alertas} />
      </div>
    </section>
  );
}

// Alertas propias (decision 0037): se crean abiertas o de una o varias
// exportaciones; se resuelven, se borran y las resueltas se pueden reabrir.
function AlertasPropias({alertas}: {alertas: AlertasDeSeguimiento | undefined}) {
  const permisos = usePermisos();
  const editar = useEditarAlerta();
  const eliminar = useEliminarAlerta();
  const [nueva, setNueva] = useState(false);
  const [verResueltas, setVerResueltas] = useState(false);
  const resueltas = useAlertasPropias('resuelta', verResueltas);
  const [error, setError] = useState<string | null>(null);
  const hacer = async (accion: () => Promise<unknown>) => {
    setError(null);
    try {
      await accion();
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };
  const fila = (a: AlertaPersonalizada, resuelta = false) => ({
    clave: String(a.id),
    tono: (resuelta ? 'neutra' : a.vencida ? 'vencida' : a.visible ? 'hoy' : 'neutra') as Tono,
    principal: (
      <div className="flex items-start justify-between gap-2">
        <span>
          <Exportaciones codigos={a.codigos} /> · {a.titulo}
        </span>
        {permisos.editar && (
          <span className="flex shrink-0 gap-2 text-xs">
            {resuelta ? (
              <button
                type="button"
                onClick={() => void hacer(() => editar.mutateAsync({id: a.id, cambios: {estado: 'activa'}}))}
                className="text-verde hover:underline"
              >
                {tr('Reabrir', 'Reopen')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void hacer(() => editar.mutateAsync({id: a.id, cambios: {estado: 'resuelta'}}))}
                className="text-verde hover:underline"
                aria-label={tr(`Resolver: ${a.titulo}`, `Resolve: ${a.titulo}`)}
              >
                ✓ {tr('Resolver', 'Resolve')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (window.confirm(tr(`¿Borrar la alerta "${a.titulo}"?`, `Delete the alert "${a.titulo}"?`)))
                  void hacer(() => eliminar.mutateAsync(a.id));
              }}
              className="text-alerta hover:underline"
              aria-label={tr(`Borrar: ${a.titulo}`, `Delete: ${a.titulo}`)}
            >
              🗑
            </button>
          </span>
        )}
      </div>
    ),
    detalle: `${fecha(a.fecha)} · ${a.tipoEvento} · ${tr(
      `aviso ${numero(a.diasAntes)} días antes`,
      `notice ${numero(a.diasAntes)} days before`,
    )}${a.notas ? ` · ${a.notas}` : ''}${a.creadaPor ? ` · ${a.creadaPor}` : ''}`,
  });
  return (
    <Tarjeta
      titulo={tr('Personalizadas', 'Custom')}
      subtitulo={tr('Hitos y vencimientos', 'Milestones and deadlines')}
      cantidad={alertas?.personalizadas.length ?? 0}
      vacio={tr('Sin alertas personalizadas activas.', 'No active custom alerts.')}
      prueba="alertas-propias"
      accion={
        permisos.editar && alertas && !nueva ? (
          <button
            type="button"
            onClick={() => setNueva(true)}
            className="text-xs font-medium text-verde hover:underline"
          >
            {tr('＋ Nueva alerta', '＋ New alert')}
          </button>
        ) : null
      }
      arriba={
        <>
          {nueva && alertas && <FormularioDeAlerta hoy={alertas.hoy} onListo={() => setNueva(false)} />}
          {error && (
            <p className="mt-2 text-xs text-alerta" role="alert">
              {error}
            </p>
          )}
        </>
      }
      filas={(alertas?.personalizadas ?? []).map(a => fila(a))}
      abajo={
        <div className="mt-2 text-xs">
          <button type="button" onClick={() => setVerResueltas(v => !v)} className="text-apagado hover:text-tinta">
            {verResueltas ? tr('▴ Ocultar resueltas', '▴ Hide resolved') : tr('▾ Ver resueltas', '▾ Show resolved')}
          </button>
          {verResueltas && (
            <ul className="mt-1.5 space-y-1.5">
              {(resueltas.data?.alertas ?? []).length === 0 && (
                <li className="text-apagado">{tr('No hay alertas resueltas.', 'No resolved alerts.')}</li>
              )}
              {(resueltas.data?.alertas ?? []).map(a => {
                const f = fila(a, true);
                return (
                  <li
                    key={f.clave}
                    className={`rounded-md border px-3 py-1.5 text-sm opacity-80 ${CLASE_DE_TONO.neutra}`}
                  >
                    <div>{f.principal}</div>
                    <div className="text-xs text-apagado">{f.detalle}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      }
    />
  );
}

// Las exportaciones de una alerta, o "Abierta" si no tiene.
function Exportaciones({codigos}: {codigos: string[]}) {
  if (!codigos.length)
    return (
      <span className="rounded-full bg-dorado-claro px-2 py-0.5 text-xs font-medium">{tr('Abierta', 'Open')}</span>
    );
  return (
    <>
      {codigos.map((c, i) => (
        <span key={c}>
          {i > 0 && ', '}
          <Enlace codigo={c} />
        </span>
      ))}
    </>
  );
}

function Enlace({codigo}: {codigo: string}) {
  return (
    <Link to={`/seguimiento/${encodeURIComponent(codigo)}`} className="font-medium text-verde hover:underline">
      {codigo}
    </Link>
  );
}

function Tarjeta({
  titulo,
  subtitulo,
  cantidad,
  vacio,
  filas,
  accion,
  arriba,
  abajo,
  prueba,
}: {
  titulo: string;
  subtitulo: string;
  cantidad: number;
  vacio: string;
  filas: Array<{clave: string; tono: Tono; principal: ReactNode; detalle: string}>;
  // Boton junto al titulo, contenido antes y despues de la lista (alertas propias).
  accion?: ReactNode;
  arriba?: ReactNode;
  abajo?: ReactNode;
  prueba?: string;
}) {
  const [todas, setTodas] = useState(false);
  const visibles = todas ? filas : filas.slice(0, VISIBLES);
  return (
    <div className="rounded-lg border border-linea bg-white p-4" data-prueba={prueba}>
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-apagado">{titulo}</div>
          <div className="text-xs text-apagado">{subtitulo}</div>
        </div>
        <div className="flex items-baseline gap-2">
          {accion}
          <span className="rounded-full bg-papel px-2 py-0.5 text-xs font-medium tabular-nums">{numero(cantidad)}</span>
        </div>
      </div>
      {arriba}
      {filas.length === 0 ? (
        <p className="mt-3 text-sm text-apagado">{vacio}</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {visibles.map(fila => (
            <li key={fila.clave} className={`rounded-md border px-3 py-1.5 text-sm ${CLASE_DE_TONO[fila.tono]}`}>
              <div>{fila.principal}</div>
              <div className="text-xs text-apagado">{fila.detalle}</div>
            </li>
          ))}
        </ul>
      )}
      {filas.length > VISIBLES && (
        <button type="button" onClick={() => setTodas(t => !t)} className="mt-2 text-xs text-verde hover:underline">
          {todas
            ? tr('Ver menos', 'Show less')
            : tr(
                `+ ${numero(filas.length - VISIBLES)} alertas adicionales`,
                `+ ${numero(filas.length - VISIBLES)} more alerts`,
              )}
        </button>
      )}
      {abajo}
    </div>
  );
}
